<script>
import {Icon} from '@iconify/vue';
import {required} from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';

/* COMPOSANTS */
import AkInputText from '@components/input/AkInputText';
import AkCheckbox from '@components/input/AkCheckbox';
import AkDialog from '@components/general/AkDialog';
import GaView from '@components/layout/GaView';

/* MIXINS */
import randomRef from '@mixins/randomRef';
import utilsMixin from '@mixins/utilsMixin';
import loaderMixin from '@mixins/loaderMixin';
import messageMixin from '@mixins/messageMixin';

/* SERVICES */
import itemLabelService from '@services/itemLabelService';

export default {
  components: {
    Icon,
    AkCheckbox,
    AkDialog,
    AkInputText,
    GaView,
  },
  mixins: [loaderMixin, messageMixin, randomRef, utilsMixin],
  setup() {
    return {v$: useVuelidate()};
  },
  head() {
    return {
      title: 'settings.filetype',
    };
  },
  data() {
    return {
      loading: true,
      list: [],
      item: {
        type: 'FILE_TYPE',
        enabled: true,
        label: '',
      },
    };
  },
  validations() {
    return {
      item: {
        label: {required},
      },
    };
  },
  mounted() {
    itemLabelService.itemLabels({type: 'FILE_TYPE'}).then((data) => {
      this.list = data;
      this.loading = false;
    });
  },
  methods: {
    create() {
      this.v$.$touch();
      if (this.v$.$error) return;

      this.showTotalLoader();
      itemLabelService
        .create(this.item)
        .then((data) => {
          this.success(this.$t('filetype.added'));
          this.list.push(data);
        })
        .finally(() => {
          this.hideLoader();
          this.$refs.dialogCreate.hide();
        });
    },
    update() {
      this.v$.$touch();
      if (this.v$.$error) return;

      this.showTotalLoader();
      itemLabelService
        .update(this.item)
        .then((data) => {
          this.replaceInListById(this.list, data);
          this.success(this.$t('filetype.updated'));
        })
        .finally(() => {
          this.hideLoader();
          this.$refs.dialogEdit.hide();
        });
    },
    delete() {
      itemLabelService
        .delete(this.item)
        .then((data) => {
          this.removeInListById(this.list, data.id);
          this.success(this.item.label + ' a bien été supprimé');
        })
        .catch((e) => {
          let content = 'Une erreur est survenue durant la suppression';
          if (e.response) {
            content = 'Une erreur est survenue durant la suppression : ' + e.response.data.error;
          }
          this.addError(content);
        });
    },

    openCreateDialog() {
      this.item = {
        type: 'FILE_TYPE',
        enabled: true,
        label: '',
      };
      this.$refs.dialogCreate.show();
    },
    openEditDialog(item) {
      this.item = {...item};
      this.$refs.dialogEdit.show();
    },
    openDeleteDialog(item) {
      this.item = item;
      this.$refs.dialogDelete.show();
    },
    increasePosition(item) {
      this.loading = true;
      itemLabelService.increasePosition(item).then(() => {
        this.increaseInListById(this.list, item.id);
        this.loading = false;
      });
    },
    decreasePosition(item) {
      this.loading = true;
      itemLabelService.decreasePosition(item).then(() => {
        this.decreaseInListById(this.list, item.id);
        this.loading = false;
      });
    },
  },
};
</script>

<template>
  <GaView :ref="ref" :title="$t('settings.filetype')">
    <template #action>
      <div class="d-flex justify-content-end">
        <button class="btn btn-primary float-right" @click="openCreateDialog()">
          <i class="gst-plus pr-1" />
          {{ $t('add') }}
        </button>
      </div>
    </template>

    <template #content>
      <DataTable
        :always-show-paginator="false"
        :loading="loading"
        :paginator="true"
        :rows="10"
        :rows-per-page-options="[10, 20, 50]"
        :value="list"
        class="table"
        current-page-report-template="{first}-{last}/{totalRecords}"
        removable-sort
        responsive-layout="scroll"
        sort-field="position"
        striped-rows>
        <template #empty>
          {{ $t('filetype.empty') }}
        </template>
        <Column :header="$t('item_label.position')" field="position" header-style="width: 130px; text-align: center">
          <template #body="slotProps">
            <div class="d-flex">
              <span
                v-if="slotProps.index !== 0"
                class="btn btn-xs btn-inverse-primary mr-1"
                @click="decreasePosition(slotProps.data)">
                <i class="fe fe-arrow-up" />
              </span>
              <span
                v-if="slotProps.index !== list.length - 1"
                :style="slotProps.index === 0 ? 'margin-left:46px;' : ''"
                class="btn btn-xs btn-inverse-primary mr-1"
                @click="increasePosition(slotProps.data)">
                <i class="fe fe-arrow-down" />
              </span>
            </div>
          </template>
        </Column>
        <Column :header="$t('item_label.label')" field="label">
          <template #body="slotProps">
            <span>{{ slotProps.data.label }}</span>
          </template>
        </Column>
        <Column :header="$t('item_label.enabled')" field="enabled">
          <template #body="slotProps">
            <i v-if="slotProps.data.enabled" class="ti ti-check text-grey-900" />
            <i v-else class="ti ti-close" />
          </template>
        </Column>
        <Column body-style="text-align: right; overflow: visible" header-style=" width: 160px; text-align: btn">
          <template #body="slotProps">
            <div class="d-flex justify-content-end">
              <span class="btn btn-xs btn-inverse-primary mr-1 pointer" @click="openEditDialog(slotProps.data)">
                <i class="fe fe-edit" />
              </span>
              <span class="btn btn-xs btn-inverse-danger pointer" @click="openDeleteDialog(slotProps.data)">
                <i class="fe fe-trash" />
              </span>
            </div>
          </template>
        </Column>
      </DataTable>
    </template>

    <template #extra>
      <AkDialog
        ref="dialogCreate"
        :title="$t('filetype.add')"
        :validate-label="$t('add')"
        width="450px"
        @validate="create()">
        <div class="form-row">
          <AkInputText
            v-model="item.label"
            :label="$t('item_label.label')"
            :validator="v$.item.label"
            class-name="col-md-12" />
          <AkCheckbox v-model="item.enabled" :label="$t('item_label.enabled')" class-name="col-md-12" />
        </div>
      </AkDialog>

      <AkDialog
        ref="dialogEdit"
        :title="$t('filetype.update')"
        :validate-label="$t('update')"
        width="450px"
        @validate="update()">
        <div class="form-row">
          <AkInputText
            v-model="item.label"
            :label="$t('item_label.label')"
            :validator="v$.item.label"
            class-name="col-md-12" />
          <AkCheckbox v-model="item.enabled" :label="$t('item_label.enabled')" class-name="col-md-12" />
        </div>
      </AkDialog>

      <AkDialog
        ref="dialogDelete"
        :auto-hide-on-validate="true"
        :cancel-label="$t('no')"
        :title="$t('delete_dialog')"
        :validate-label="$t('yes')"
        width="450px"
        @validate="this.delete()">
        <div class="flex items-center gap-2">
          <Icon icon="fluent:warning-20-regular" class="text-red" width="26" />
          <span>{{ $t('filetype.confirm_delete') }}</span>
        </div>
      </AkDialog>
    </template>
  </GaView>
</template>
